import { Component, OnInit } from '@angular/core';
import { finalize, forkJoin } from 'rxjs';
import {
  ApiClient,
  IntegrationEntityType,
  IntegrationField,
  IntegrationSystem,
} from 'src/app/shared/models/integration.models';
import { IntegrationService } from 'src/app/shared/services/integration.service';
import {
  TableAction,
  TableColumn,
} from 'src/app/shared/models/table-config.model';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IntegrationSystemModalComponent } from './integration-system-modal/integration-system-modal.component';
import { IntegrationFieldModalComponent } from './integration-field-modal/integration-field-modal.component';
import { ApiClientModalComponent } from './api-client-modal/api-client-modal.component';

type IntegrationTab = 'systems' | 'fields' | 'api-clients';

@Component({
  selector: 'app-integrations',
  templateUrl: './integrations.component.html',
  styleUrls: ['./integrations.component.scss'],
  standalone: false,
  providers: [DialogService],
})
export class IntegrationsComponent implements OnInit {
  activeTab: IntegrationTab = 'systems';

  systems: IntegrationSystem[] = [];
  filteredSystems: any[] = [];
  entityTypes: IntegrationEntityType[] = [];
  fields: IntegrationField[] = [];
  filteredFields: any[] = [];
  apiClients: ApiClient[] = [];
  filteredApiClients: any[] = [];

  selectedSystemCode = '';
  selectedEntityType = '';
  searchText = '';

  loadingSystems = false;
  loadingFields = false;
  loadingApiClients = false;

  private dialogRef: DynamicDialogRef | undefined;

  readonly systemColumns: TableColumn[] = [
    {
      field: 'code',
      header: 'CODE',
      type: 'text',
      sortable: true,
      linkAction: (row) => this.openSystemModal(row),
      width: '180px',
    },
    { field: 'name', header: 'NAME', type: 'text', sortable: true },
    {
      field: 'tenantName',
      header: 'TENANT',
      type: 'text',
      sortable: true,
      width: '220px',
    },
    {
      field: 'enabledLabel',
      header: 'STATUS',
      type: 'status',
      width: '110px',
      statusConfig: {
        Enabled: { cssClass: 'bg-emerald-50 text-emerald-700' },
        Disabled: { cssClass: 'bg-gray-100 text-gray-600' },
      },
    },
    { field: 'actions', header: '', type: 'actions', width: '80px' },
  ];

  readonly fieldColumns: TableColumn[] = [
    {
      field: 'tenantName',
      header: 'TENANT',
      type: 'text',
      sortable: true,
      width: '180px',
    },
    {
      field: 'integrationSystemCode',
      header: 'SYSTEM',
      type: 'text',
      sortable: true,
      width: '150px',
    },
    {
      field: 'entityTypeDisplayName',
      header: 'ENTITY',
      type: 'text',
      sortable: true,
      width: '170px',
    },
    {
      field: 'externalKey',
      header: 'KEY',
      type: 'text',
      sortable: true,
      linkAction: (row) => this.openFieldModal(row),
      width: '180px',
    },
    { field: 'label', header: 'LABEL', type: 'text', sortable: true },
    {
      field: 'dataType',
      header: 'TYPE',
      type: 'text',
      sortable: true,
      width: '100px',
    },
    {
      field: 'visibleLabel',
      header: 'FORM',
      type: 'status',
      width: '95px',
      statusConfig: {
        Visible: { cssClass: 'bg-blue-50 text-blue-700' },
        Hidden: { cssClass: 'bg-gray-100 text-gray-600' },
      },
    },
    {
      field: 'enabledLabel',
      header: 'STATUS',
      type: 'status',
      width: '110px',
      statusConfig: {
        Enabled: { cssClass: 'bg-emerald-50 text-emerald-700' },
        Disabled: { cssClass: 'bg-gray-100 text-gray-600' },
      },
    },
    { field: 'actions', header: '', type: 'actions', width: '80px' },
  ];

  readonly apiClientColumns: TableColumn[] = [
    {
      field: 'name',
      header: 'NAME',
      type: 'text',
      sortable: true,
      linkAction: (row) => this.openApiClientModal(row),
      width: '220px',
    },
    {
      field: 'tenantName',
      header: 'TENANT',
      type: 'text',
      sortable: true,
      width: '220px',
    },
    {
      field: 'expiresAt',
      header: 'EXPIRES AT',
      type: 'date',
      sortable: true,
      dateFormat: 'dd/MM/yyyy',
      width: '150px',
    },
    {
      field: 'allowedPermissionCount',
      header: 'ENDPOINTS',
      type: 'count-badge',
      tooltipField: 'allowedPermissionKeys',
      width: '120px',
    },
    {
      field: 'statusLabel',
      header: 'STATUS',
      type: 'status',
      width: '120px',
      statusConfig: {
        Enabled: { cssClass: 'bg-emerald-50 text-emerald-700' },
        Disabled: { cssClass: 'bg-gray-100 text-gray-600' },
        Expired: { cssClass: 'bg-amber-50 text-amber-700' },
      },
    },
    { field: 'actions', header: '', type: 'actions', width: '80px' },
  ];

  readonly systemActions: TableAction[] = [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: (row) => this.openSystemModal(row),
    },
  ];

  readonly fieldActions: TableAction[] = [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: (row) => this.openFieldModal(row),
    },
    {
      label: 'Disable',
      icon: 'pi pi-ban',
      styleClass: 'p-button-text p-button-danger',
      visible: (row) => row.enabled,
      command: (row) => this.disableField(row),
    },
  ];

  readonly apiClientActions: TableAction[] = [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: (row) => this.openApiClientModal(row),
    },
  ];

  constructor(
    private readonly integrationService: IntegrationService,
    private readonly toastService: AppToastService,
    private readonly dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.loadInitialData();
  }

  onTabChange(value: string | number): void {
    this.activeTab = String(value) as IntegrationTab;
    this.searchText = '';
    this.applyFilters();
  }

  handleSearch(text: string): void {
    this.searchText = text?.trim().toLowerCase() || '';
    this.applyFilters();
  }

  handleNew(): void {
    if (this.activeTab === 'systems') {
      this.openSystemModal();
      return;
    }

    if (this.activeTab === 'api-clients') {
      this.openApiClientModal();
      return;
    }

    this.openFieldModal();
  }

  loadInitialData(): void {
    this.loadingSystems = true;
    this.loadingFields = true;
    this.loadingApiClients = true;

    forkJoin({
      systems: this.integrationService.getSystems(null, true),
      entityTypes: this.integrationService.getEntityTypes(),
      apiClients: this.integrationService.getApiClients(null, true),
    })
      .pipe(
        finalize(() => {
          this.loadingSystems = false;
          this.loadingFields = false;
          this.loadingApiClients = false;
        }),
      )
      .subscribe({
        next: ({ systems, entityTypes, apiClients }) => {
          this.systems = systems || [];
          this.entityTypes = entityTypes || [];
          this.apiClients = apiClients || [];
          this.selectedSystemCode =
            this.systems.find((system) => system.code === 'finpac_middleware')?.code ||
            this.systems[0]?.code ||
            '';
          this.loadFields();
          this.applyFilters();
        },
      });
  }

  loadSystems(): void {
    this.loadingSystems = true;
    this.integrationService
      .getSystems(null, true)
      .pipe(finalize(() => (this.loadingSystems = false)))
      .subscribe({
        next: (systems) => {
          this.systems = systems || [];
          this.applyFilters();
        },
      });
  }

  loadFields(): void {
    this.loadingFields = true;
    this.integrationService
      .getFields({
        systemCode: this.selectedSystemCode || null,
        entityType: this.selectedEntityType || null,
        includeDisabled: true,
        includeAllTenants: true,
      })
      .pipe(finalize(() => (this.loadingFields = false)))
      .subscribe({
        next: (fields) => {
          this.fields = fields || [];
          this.applyFilters();
        },
      });
  }

  loadApiClients(): void {
    this.loadingApiClients = true;
    this.integrationService
      .getApiClients(null, true)
      .pipe(finalize(() => (this.loadingApiClients = false)))
      .subscribe({
        next: (clients) => {
          this.apiClients = clients || [];
          this.applyFilters();
        },
      });
  }

  openSystemModal(system?: IntegrationSystem): void {
    this.dialogRef = this.dialogService.open(IntegrationSystemModalComponent, {
      showHeader: false,
      width: '560px',
      contentStyle: { padding: '0', borderRadius: '12px', overflow: 'hidden' },
      baseZIndex: 10000,
      data: { system },
    });

    this.dialogRef.onClose.subscribe((result: string) => {
      if (result === 'success') {
        this.loadSystems();
      }
    });
  }

  openFieldModal(field?: IntegrationField): void {
    this.dialogRef = this.dialogService.open(IntegrationFieldModalComponent, {
      showHeader: false,
      width: '760px',
      contentStyle: { padding: '0', borderRadius: '12px', overflow: 'hidden' },
      baseZIndex: 10000,
      data: {
        field,
        systems: this.systems,
        entityTypes: this.entityTypes,
        selectedSystemCode: this.selectedSystemCode,
      },
    });

    this.dialogRef.onClose.subscribe((result: string) => {
      if (result === 'success') {
        this.loadFields();
      }
    });
  }

  openApiClientModal(client?: ApiClient): void {
    this.dialogRef = this.dialogService.open(ApiClientModalComponent, {
      showHeader: false,
      width: '880px',
      contentStyle: { padding: '0', borderRadius: '12px', overflow: 'hidden' },
      baseZIndex: 10000,
      data: { client },
    });

    this.dialogRef.onClose.subscribe((result: { refresh?: boolean } | string) => {
      if (result === 'success' || (result as { refresh?: boolean })?.refresh) {
        this.loadApiClients();
      }
    });
  }

  disableField(field: IntegrationField): void {
    this.integrationService.deleteField(field.id).subscribe({
      next: () => {
        this.toastService.success('Field disabled', 'Integration field was disabled.');
        this.loadFields();
      },
    });
  }

  applyFilters(): void {
    const systemRows = this.systems.map((system) => ({
      ...system,
      enabledLabel: system.enabled ? 'Enabled' : 'Disabled',
    }));

    this.filteredSystems = this.searchText
      ? systemRows.filter((system) =>
          [system.code, system.name, system.tenantName, system.enabledLabel]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(this.searchText)),
        )
      : systemRows;

    const fieldRows = this.fields.map((field) => ({
      ...field,
      entityTypeDisplayName: field.entityTypeDisplayName || field.entityType,
      visibleLabel: field.visibleInForm ? 'Visible' : 'Hidden',
      enabledLabel: field.enabled ? 'Enabled' : 'Disabled',
    }));

    this.filteredFields = this.searchText
      ? fieldRows.filter((field) =>
          [
            field.integrationSystemCode,
            field.tenantName,
            field.entityTypeDisplayName,
            field.externalKey,
            field.label,
            field.dataType,
            field.visibleLabel,
            field.enabledLabel,
          ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(this.searchText)),
        )
      : fieldRows;

      const apiClientRows = this.apiClients.map((client) => ({
        ...client,
        allowedPermissionCount: client.allowedPermissionKeys?.length || 0,
        statusLabel: client.enabled
          ? new Date(client.expiresAt).getTime() < Date.now()
            ? 'Expired'
            : 'Enabled'
          : 'Disabled',
      }));

      this.filteredApiClients = this.searchText
        ? apiClientRows.filter((client) =>
            [
              client.name,
              client.tenantName,
              client.statusLabel,
              (client.allowedPermissionKeys || []).join(' '),
            ]
              .filter(Boolean)
              .some((value) => String(value).toLowerCase().includes(this.searchText)),
          )
        : apiClientRows;
  }

  formatSystemOption(system: IntegrationSystem): string {
    const tenantName = system.tenantName ? `${system.tenantName} - ` : '';
    return `${tenantName}${system.name} (${system.code})`;
  }
}
